import { warehouseClient } from "@/infrastructure/http/warehouseClient";
import { CONFIG } from "@/config";

declare const __SYNC_LAMBDA_URL__: string | undefined;

const SYNC_LAMBDA_URL = typeof __SYNC_LAMBDA_URL__ !== "undefined" ? __SYNC_LAMBDA_URL__ : "http://localhost:3900";

export type SyncEntity = "WAREHOUSES" | "PRODUCTS" | "PURCHASES" | "INVOICES";
export type SyncStatusType = "sync_warehouses" | "sync_products" | "sync_purchases" | "sync_invoices";
export type SyncJobStatus = "COMPLETED" | "PROCESSING" | "FAILED" | "PENDING";

export interface SyncResult {
  tenantId: string;
  provider: string;
  entity: string;
  status: string;
  totalRows: number;
  processed: number;
  created: number;
  skipped: number;
  duration: number;
  jobId: string;
}

export interface SyncJobRecord {
  id: string;
  type: string;
  status: SyncJobStatus;
  totalRows: number;
  processed: number;
  created: number;
  skipped: number;
  errors: any[] | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface SyncParams {
  tenantId: string;
  userId: string;
  syncEntity: SyncEntity;
  typeUseId?: string;
  typeUnitId?: string;
  categoryId?: string;
}

export async function triggerSync(params: SyncParams): Promise<SyncResult> {
  const response = await fetch(`${SYNC_LAMBDA_URL}/sync`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${getToken()}`,
    },
    body: JSON.stringify(params),
  });
  const data = await response.json();
  // Lambda response has body as string
  if (typeof data.body === "string") return JSON.parse(data.body);
  return data;
}

export async function getSyncStatus(tenantId: string, type: SyncStatusType): Promise<SyncJobRecord | null> {
  try {
    const response = await warehouseClient.get<any>(
      `/product/import/by-tenant/${tenantId}?type=${type}&limit=1`
    );
    const records = response.data?.data ?? response.data ?? [];
    if (Array.isArray(records) && records.length > 0) return records[0];
    return null;
  } catch {
    return null;
  }
}

export async function getTypeUsesByTenant(tenantId: string): Promise<{ id: string; name: string }[]> {
  try {
    const response = await warehouseClient.get<any>(`/type-use/by-tenant/${tenantId}`);
    return response.data?.data ?? response.data ?? [];
  } catch {
    return [];
  }
}

export async function getTypeUnitsByTenant(tenantId: string): Promise<{ id: string; code: string; name: string; abbreviation: string }[]> {
  try {
    const response = await warehouseClient.get<any>(`/type-unit/by-tenant/${tenantId}`);
    return response.data?.data ?? response.data ?? [];
  } catch {
    return [];
  }
}

export async function getCategoriesByTenant(tenantId: string): Promise<{ id: string; name: string; code: string }[]> {
  try {
    const response = await warehouseClient.get<any>(`/category/by-tenant/${tenantId}?status=ACTIVE`);
    const raw = response.data?.data;
    if (raw && Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw)) return raw;
    return [];
  } catch {
    return [];
  }
}

function getToken(): string {
  try {
    return localStorage.getItem("nexo-token") ?? "";
  } catch {
    return "";
  }
}
